/** Backup center (§3.2) — written for someone who has never opened a developer tool. */
import { useEffect, useState } from 'preact/hooks';
import { getDb } from '../../core/db';
import {
  autoExportSupported, buildExportJson, buildImportPreview, chooseAutoExportDir,
  commitImport, exportFilename, markExported, needsExportNag, runAutoExport, type ImportPreview,
} from '../../core/backup';
import { refreshCore, settingsSig, toast } from '../state';
import { Banner } from '../bits';

export function BackupView() {
  const s = settingsSig.value;
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importErr, setImportErr] = useState<string | null>(null);
  const [snapCount, setSnapCount] = useState<number>(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => { setSnapCount(await (await getDb()).count('snapshots')); })();
  }, [s?.lastSnapshotAt]);

  async function exportNow() {
    setBusy(true);
    try {
      const json = await buildExportJson();
      const blob = new Blob([json], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = exportFilename();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      await markExported();
      await refreshCore();
      toast('Backup file saved to your Downloads');
    } finally { setBusy(false); }
  }

  async function pickFile(e: Event) {
    setImportErr(null); setPreview(null);
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      setPreview(await buildImportPreview(await file.text()));
    } catch (err) {
      setImportErr((err as Error).message);
    }
    input.value = '';
  }

  async function doImport() {
    if (!preview) return;
    setBusy(true);
    try {
      await commitImport(preview);
      setPreview(null);
      await refreshCore();
      toast('Backup restored — everything in the file is now live');
    } catch (err) {
      setImportErr(`The import did not finish and nothing was changed. ${(err as Error).message}`);
    } finally { setBusy(false); }
  }

  async function setupAutoExport() {
    try {
      if (await chooseAutoExportDir()) {
        await runAutoExport();
        await refreshCore();
        toast('Auto-backup folder set — a fresh file is written daily while the app is open');
      }
    } catch { /* user cancelled the picker — nothing to do */ }
  }

  const nag = s && needsExportNag(s.lastExportAt);

  return (
    <div style="max-width:760px;">
      <div class="pagehead"><h1>Backup &amp; restore</h1></div>

      {nag && (
        <Banner>
          <b>It has been more than 14 days since your last export{s?.lastExportAt ? '' : ' (none yet)'}.</b>{' '}
          Click <b>Export everything</b> below — it takes one click and protects every job and price.
        </Banner>
      )}

      <section class="card" style="margin-bottom:16px;">
        <h2 style="margin-bottom:8px;">How your data is protected</h2>
        <p class="small">
          Everything lives on this device, in this browser. The app keeps <b>{snapCount} automatic snapshots</b> here
          (latest: {s?.lastSnapshotAt ? new Date(s.lastSnapshotAt).toLocaleString() : 'none yet'}), and a snapshot is taken a few
          seconds after every change. Snapshots protect against mistakes — they do not protect against a lost or broken device.
          The export file below does. Keep one somewhere safe every couple of weeks.
        </p>
      </section>

      <section class="card" style="margin-bottom:16px;">
        <h2 style="margin-bottom:8px;">Export — one file with everything</h2>
        <p class="small" style="margin-bottom:10px;">
          Last export: <b>{s?.lastExportAt ? new Date(s.lastExportAt).toLocaleString() : 'never'}</b>.
          The file contains jobs, clients, prices, documents, and photos. Anyone with the file can read your business data, so store it like a business record.
        </p>
        <button class="btn btn-primary" disabled={busy} onClick={() => void exportNow()}>Export everything</button>
        {autoExportSupported() ? (
          <span style="margin-left:10px;">
            <button class="btn btn-ghost" disabled={busy} onClick={() => void setupAutoExport()}>
              {s?.autoExport ? 'Change auto-backup folder…' : 'Also back up to a folder automatically…'}
            </button>
          </span>
        ) : (
          <p class="small muted" style="margin-top:8px;">Automatic folder backup needs Chrome or Edge on a computer. On this browser, use the export button.</p>
        )}
      </section>

      <section class="card">
        <h2 style="margin-bottom:8px;">Restore from a backup file</h2>
        <p class="small" style="margin-bottom:10px;">Pick a backup file and you will see exactly what it contains before anything is replaced. Old-version files are upgraded automatically.</p>
        <input type="file" accept=".json,application/json" onChange={(e) => void pickFile(e)} aria-label="Choose backup file" />
        {importErr && <Banner onClose={() => setImportErr(null)}>{importErr}</Banner>}
        {preview && (
          <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm restore">
            <div class="modal">
              <h2 style="margin-bottom:10px;">Replace current data with this file?</h2>
              <p class="small" style="margin-bottom:10px;">
                The file contains <b>{preview.counts.jobs} jobs</b>, {preview.counts.takeoffLines} takeoff lines,{' '}
                {preview.counts.inventoryItems} inventory items, {preview.counts.documents} documents,{' '}
                {preview.counts.photos} photos, and {preview.counts.priceItems} price book items.
              </p>
              {preview.warnings.map((w) => <Banner kind="info" key={w}>{w}</Banner>)}
              <p class="small" style="margin-bottom:14px;">
                <b>Everything currently in the app will be replaced.</b> A snapshot of today's data is kept automatically, and nothing changes until you click Restore.
              </p>
              <div style="display:flex;gap:8px;justify-content:flex-end;">
                <button class="btn btn-ghost" onClick={() => setPreview(null)}>Cancel — keep current data</button>
                <button class="btn btn-danger" disabled={busy} onClick={() => void doImport()}>Restore from file</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
